import type { Locale } from "./i18n";

// Phase 9: two-part feedback flow (one random rating question, then an optional freeform
// question), posted directly client-side to the existing external feedback endpoint — no new
// backend code, matching today's architecture exactly. Appendix C's question bank already has
// full fi/en/sv content (unlike AI-PT, nothing new to author here).

const FEEDBACK_ENDPOINT = "https://25k9t136h9.execute-api.eu-west-1.amazonaws.com/prod/feedback";

interface Question {
  id: string;
  text: Record<Locale, string>;
}

// IDs 1/2/4/5 (not sequential — 3 is reserved for the freeform question below), one chosen at
// random per widget load, exactly like the old app.
export const RATING_QUESTIONS: Question[] = [
  {
    id: "1",
    text: {
      en: "How well does the gym equipment support your training?",
      fi: "Kuinka hyvin kuntosalin laitteet tukevat harjoitteluasi?",
      sv: "Hur väl stödjer gymutrustningen din träning?",
    },
  },
  {
    id: "2",
    // The scale hint is part of the question, as in the old app: a yes/no question on stars
    // is ambiguous otherwise, and the backend's crowding KPI assumes 5 = not crowded.
    text: {
      en: "Has the gym felt crowded lately? (1 = crowded, 5 = not crowded)",
      fi: "Onko kuntosali tuntunut ruuhkaiselta viime aikoina? (1 = ruuhkaa, 5 = ei ruuhkaa)",
      sv: "Har gymmet känts trångt på sistone? (1 = trångt, 5 = inte trångt)",
    },
  },
  {
    id: "4",
    text: {
      en: "How happy are you with the gym?",
      fi: "Kuinka tyytyväinen olet kuntosaliin?",
      sv: "Hur nöjd är du med gymmet?",
    },
  },
  {
    id: "5",
    text: {
      en: "Would you recommend this gym to a friend?",
      fi: "Suosittelisitko tätä kuntosalia ystävälle?",
      sv: "Skulle du rekommendera detta gym till en vän?",
    },
  },
];

const FREEFORM_QUESTION: Question = {
  id: "3",
  text: {
    en: "What would you change?",
    fi: "Miten parantaisit?",
    sv: "Vad skulle du ändra?",
  },
};

const CHROME: Record<Locale, { title: string; submit: string; skip: string; placeholder: string; thanks: string }> = {
  en: { title: "Feedback", submit: "Submit", skip: "Skip", placeholder: "Tell us more (optional)", thanks: "Thanks for your feedback!" },
  fi: { title: "Palaute", submit: "Lähetä", skip: "Ohita", placeholder: "Kerro lisää (valinnainen)", thanks: "Kiitos palautteesta!" },
  sv: { title: "Feedback", submit: "Skicka", skip: "Hoppa över", placeholder: "Berätta mer (valfritt)", thanks: "Tack för din feedback!" },
};

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char]!);
}

async function postFeedback(payload: Record<string, unknown>): Promise<void> {
  try {
    await fetch(FEEDBACK_ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error("[ii-gp-widget] feedback submission failed to send", error);
  }
}

/** Exact payload shape from Appendix C — note `score` is a *string* for the rating request
 * but a *number* (always 0) for the freeform one; that asymmetry is in the original spec, not
 * a typo here. */
export function buildRatingPayload(
  premiseId: number | string,
  locale: Locale,
  randomUid: string,
  question: Question,
  score: number,
): Record<string, unknown> {
  return {
    premise_id: String(premiseId),
    question_id: question.id,
    question_part: 1,
    question_text: question.text[locale],
    score: String(score),
    random_uid: randomUid,
    lang: locale,
    question_type: "score",
    text_answer: "",
  };
}

export function buildFreeformPayload(
  premiseId: number | string,
  locale: Locale,
  randomUid: string,
  textAnswer: string,
): Record<string, unknown> {
  return {
    premise_id: String(premiseId),
    question_id: FREEFORM_QUESTION.id,
    question_part: 2,
    question_text: FREEFORM_QUESTION.text[locale],
    score: 0,
    random_uid: randomUid,
    lang: locale,
    question_type: "freeform",
    text_answer: textAnswer,
  };
}

export interface FeedbackContext {
  premiseId: number | string;
  locale: Locale;
}

/** Title, then the stars, then the question under them — the stars are the action, the
 *  question explains what they rate. */
function renderStars(question: Question, locale: Locale): string {
  const stars = [1, 2, 3, 4, 5]
    .map((value) => `<button type="button" class="ii-gp-feedback__star" data-score="${value}" aria-label="${value}">★</button>`)
    .join("");
  return `
<div class="ii-gp-feedback__rating">
  <p class="ii-gp-feedback__title">${escapeHtml(CHROME[locale].title)}</p>
  <div class="ii-gp-feedback__stars">${stars}</div>
  <p class="ii-gp-feedback__question">${escapeHtml(question.text[locale])}</p>
</div>`;
}

function renderFreeform(locale: Locale): string {
  const chrome = CHROME[locale];
  return `
<div class="ii-gp-feedback__freeform">
  <p class="ii-gp-feedback__title">${escapeHtml(chrome.title)}</p>
  <p class="ii-gp-feedback__question">${escapeHtml(FREEFORM_QUESTION.text[locale])}</p>
  <textarea class="ii-gp-feedback__textarea" placeholder="${escapeHtml(chrome.placeholder)}" rows="2"></textarea>
  <div class="ii-gp-feedback__actions">
    <button type="button" class="ii-gp-feedback__skip">${escapeHtml(chrome.skip)}</button>
    <button type="button" class="ii-gp-feedback__submit">${escapeHtml(chrome.submit)}</button>
  </div>
</div>`;
}

export function mountFeedbackWidget(container: HTMLElement, context: FeedbackContext): void {
  const { locale } = context;
  const randomUid = crypto.randomUUID(); // one per widget load, shared by both parts — replaces the old duplicated guidv4()
  const question = RATING_QUESTIONS[Math.floor(Math.random() * RATING_QUESTIONS.length)];

  container.innerHTML = renderStars(question, locale);

  container.querySelectorAll<HTMLButtonElement>(".ii-gp-feedback__star").forEach((star) => {
    star.addEventListener("click", () => {
      const score = Number(star.dataset.score);
      void postFeedback(buildRatingPayload(context.premiseId, locale, randomUid, question, score));

      container.innerHTML = renderFreeform(locale);
      const textarea = container.querySelector<HTMLTextAreaElement>(".ii-gp-feedback__textarea")!;
      const finish = () => {
        container.innerHTML = `<p class="ii-gp-feedback__thanks">${escapeHtml(CHROME[locale].thanks)}</p>`;
      };
      container.querySelector(".ii-gp-feedback__skip")?.addEventListener("click", finish);
      container.querySelector(".ii-gp-feedback__submit")?.addEventListener("click", () => {
        const text = textarea.value.trim();
        if (text) void postFeedback(buildFreeformPayload(context.premiseId, locale, randomUid, text)); // sent only if non-empty
        finish();
      });
    });
  });
}
